import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../../utils/api';
import { ListPageLayout } from '../../components/ListPage';
import { tokens } from '../../theme/tokens';

export default function EducationExams() {
  const [exams, setExams] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingExam, setEditingExam] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    start_date: '',
    end_date: '',
    year_period: '',
    show_in_registration: false,
    course_ids: [],
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    fetchExams();
  }, [page, rowsPerPage]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (page === 0) {
        fetchExams();
      } else {
        setPage(0);
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [search]);

  const fetchCourses = async () => {
    try {
      const response = await api.get('/education/courses', { params: { limit: 1000 } });
      setCourses(response.data.data || []);
    } catch (err) {
      console.error('Error fetching courses:', err);
    }
  };

  const fetchExams = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/education/exams', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: search || undefined,
        },
      });
      setExams(response.data.data || []);
      setTotal(Number(response.data.meta?.total) || 0);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch exams');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (exam = null) => {
    setEditingExam(exam);
    setFormData({
      name: exam?.name || '',
      start_date: exam?.start_date ? exam.start_date.split('T')[0] : '',
      end_date: exam?.end_date ? exam.end_date.split('T')[0] : '',
      year_period: exam?.year_period || '',
      show_in_registration: exam?.show_in_registration === 1 || false,
      course_ids: exam?.course_ids || [],
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingExam(null);
    setFormData({
      name: '',
      start_date: '',
      end_date: '',
      year_period: '',
      show_in_registration: false,
      course_ids: [],
    });
  };

  const handleCourseToggle = (courseId) => {
    setFormData({
      ...formData,
      course_ids: formData.course_ids.includes(courseId)
        ? formData.course_ids.filter((id) => id !== courseId)
        : [...formData.course_ids, courseId],
    });
  };

  const handleSubmit = async () => {
    try {
      // Validate required fields
      if (!formData.name || !formData.start_date || !formData.end_date || !formData.year_period) {
        setError('Name, Start Date, End Date, and Year Period are required');
        return;
      }

      const data = {
        name: formData.name.trim(),
        start_date: formData.start_date,
        end_date: formData.end_date,
        year_period: formData.year_period.trim(),
        show_in_registration: formData.show_in_registration ? 1 : 0,
        course_ids: Array.isArray(formData.course_ids) ? formData.course_ids : [],
      };
      
      if (editingExam) {
        await api.put(`/education/exams/${editingExam.id}`, data);
      } else {
        await api.post('/education/exams', data);
      }
      handleCloseDialog();
      fetchExams();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save exam');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this exam?')) {
      return;
    }
    try {
      await api.delete(`/education/exams/${id}`);
      fetchExams();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete exam');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <>
    <ListPageLayout
      title="Exams"
      crumbs={
        <>
          Operations / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>Exams</Box>
        </>
      }
      searchPlaceholder="Search exams..."
      searchValue={search}
      onSearchChange={setSearch}
      error={error}
      onErrorClose={() => setError('')}
      actions={
        <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Exam
          </Button>
      }
    >
      <TableContainer component={Paper} sx={{ overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Year Period</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {exams.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        No records yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  exams.map((exam) => (
                    <TableRow key={exam.id} hover>
                      <TableCell>{exam.name}</TableCell>
                      <TableCell>{formatDate(exam.start_date)}</TableCell>
                      <TableCell>{formatDate(exam.end_date)}</TableCell>
                      <TableCell>{exam.year_period || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={exam.show_in_registration === 1 ? 'Active' : 'Inactive'}
                          color={exam.show_in_registration === 1 ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(exam)}
                          title="Edit"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(exam.id)}
                          title="Delete"
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </TableContainer>
    </ListPageLayout>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingExam ? 'Edit Exam' : 'Add Exam'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Year Period"
                value={formData.year_period}
                onChange={(e) => setFormData({ ...formData, year_period: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.show_in_registration}
                    onChange={(e) => setFormData({ ...formData, show_in_registration: e.target.checked })}
                  />
                }
                label="Show in Registration"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Courses
              </Typography>
              <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #ddd', p: 1, borderRadius: 1 }}>
                {courses.map((course) => (
                  <FormControlLabel
                    key={course.id}
                    control={
                      <Checkbox
                        checked={formData.course_ids.includes(course.id)}
                        onChange={() => handleCourseToggle(course.id)}
                      />
                    }
                    label={course.name}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!formData.name}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
